import { PdpSectionSlotEnum } from '../../../types/cmsEnums';
import { CmsProduct } from '../../../types/cmsProduct';
import { MaterialIconName } from '../../../types/materialIconName';
import { tr } from '../../../utils/translations/tr';
import { ProductDetailsSectionConfigured } from '../Sections/Configured/ProductDetailsSectionConfigured';
import { ProductDetailsSectionConfiguredHighlights } from '../Sections/Configured/ProductDetailsSectionConfiguredHighlights';
import { ProductDetailsSectionConfiguredOffers } from '../Sections/Configured/ProductDetailsSectionConfiguredOffers';
import {
  ConfiguredSectionProps,
  PdpSectionConfig,
} from '../Sections/Configured/types';
import { ProductDetailsStickyFooter } from '../Sections/StickyFooter/ProductDetailsStickyFooter';
import { StickyNavigationBar } from '../Sections/StickyNavigationBar/StickyNavigationBar';
import {
  resolveAttributeRow,
  ResolvedGroup,
  ResolvedRow,
} from '../utils/resolveAttributeRow/resolveAttributeRow';
import { visibleSections } from '../utils/sections';
import {
  hasLongDescription,
  hasProductOffers,
} from '../utils/sectionVisibility/sectionVisibility';
import { buildDefaultSections } from './buildDefaultSections';
import { SectionBuilder, SectionCandidate } from './types';

/**
 * Static facts about each fixed slot of the configured layout (CMS-1935): the DOM / nav id, the
 * header icon and the fallback tab label used when the editor left `tab_label` empty.
 */
type SlotMeta = {
  /** Short section id; the DOM anchor is `section-${id}`. */
  id: string;
  /** Material icon name for the section header. */
  icon: MaterialIconName;
  /** `BaseTranslations` key used when the editor left `tab_label` empty. */
  fallbackLabelKey: string;
};

const SLOT_META: Record<PdpSectionSlotEnum, SlotMeta> = {
  OFFERS: { id: 'offers', icon: 'diamond', fallbackLabelKey: 'pd_nav_offers' },
  HIGHLIGHTS: {
    id: 'highlights',
    icon: 'auto_awesome',
    fallbackLabelKey: 'pd_nav_highlights',
  },
  RATES_FEES: {
    id: 'rates-fees',
    icon: 'sell',
    fallbackLabelKey: 'pd_nav_rates_fees',
  },
  FEATURES_PLATFORM: {
    id: 'features-platform',
    icon: 'devices_other',
    fallbackLabelKey: 'pd_nav_features_platform',
  },
  ELIGIBILITY_APPLY: {
    id: 'eligibility-apply',
    icon: 'new_releases',
    fallbackLabelKey: 'pd_nav_eligibility',
  },
};

const resolveGroups = (
  product: CmsProduct | undefined,
  section: PdpSectionConfig,
): ResolvedGroup[] =>
  section.groups
    .map((group) => ({
      id: group.id,
      title: group.title?.trim() || undefined,
      collapsible: group.collapsible,
      rows: group.rows
        .map((row) => resolveAttributeRow(product, row))
        .filter((row): row is ResolvedRow => row !== undefined),
    }))
    // A group with no resolvable rows hides (graceful degrade per instance / product).
    .filter((group) => group.rows.length > 0);

/**
 * Picks the section component for a slot and whether it has anything to show. By default a section
 * is "groups of attribute rows, nothing else"; Offers (reward component first) and Highlights
 * ("About this product" after the groups) also show with their fixed part alone.
 */
const toSection = (
  id: string,
  section: ConfiguredSectionProps,
): SectionCandidate => {
  const hasGroups = section.groups.length > 0;
  switch (section.slot) {
    case 'OFFERS':
      return {
        id,
        label: section.label,
        visible: hasProductOffers(section.product) || hasGroups,
        render: () => <ProductDetailsSectionConfiguredOffers {...section} />,
      };
    case 'HIGHLIGHTS':
      return {
        id,
        label: section.label,
        visible: hasGroups || hasLongDescription(section.product),
        render: () => <ProductDetailsSectionConfiguredHighlights {...section} />,
      };
    default:
      return {
        id,
        label: section.label,
        visible: hasGroups,
        render: () => <ProductDetailsSectionConfigured {...section} />,
      };
  }
};

/**
 * STK / BKA (investments, banking): the editor-configured sections (CMS-1935 `sections`, which come
 * with the module's page data) in editor order, each group's rows resolved against the product, and
 * only the sections with content kept — so the sticky nav and the rendered sections always agree.
 * Sections the editor force-hid are skipped up front, and a slot used twice keeps its first section
 * only (the Strapi rules forbid duplicates; this guards data that arrived another way, since the slot
 * is the DOM anchor and the React key). A module without configured sections renders the default
 * builder's sections.
 */
export const buildStkBkaSections: SectionBuilder = (props, staticText) => {
  if (!props.sections?.length) {
    return buildDefaultSections(props, staticText);
  }

  const { product, vertical, pushDataLayer } = props;
  const seenSlots = new Set<PdpSectionSlotEnum>();

  const sections = props.sections
    .filter((section) => section.visible !== false)
    .flatMap((config) => {
      const meta = SLOT_META[config.slot];
      // A slot this build does not know (e.g. a slot added in Strapi before the frontend
      // caught up) is skipped rather than crashing the page.
      if (!meta) return [];
      if (seenSlots.has(config.slot)) return [];
      seenSlots.add(config.slot);

      return [
        toSection(meta.id, {
          slot: config.slot,
          label:
            config.tabLabel?.trim() || tr(meta.fallbackLabelKey, staticText),
          icon: meta.icon,
          groups: resolveGroups(product, config),
          product,
          vertical,
          pushDataLayer,
        }),
      ];
    });

  return {
    Navigation: StickyNavigationBar,
    Footer: ProductDetailsStickyFooter,
    sections: visibleSections(sections),
  };
};
